<template>
  <a-modal
    v-model:open="visable"
    title="选择保留的团队成员"
    destroyOnClose
    :maskClosable="false"
    :closable="false"
    :footer="null"
    wrapClassName="group-retain-pop important-popup"
    :width="670"
    centered
    :z-index="1000"
    :afterClose="onClose"
    :keyboard="false"
  >
    <div class="modal-body">
      <div class="top-box">
        <div>
          <span class="top-box-text">本次续费的席位数少于当前团队人数，您需要选择保留哪些成员。离开的成员的创作资产将转移至管理员的 </span>
          <span class="top-box-bold">团队空间-个人空间中</span>
        </div>
      </div>
      <div class="content-box">
        <div class="form-item">
          <div class="form-item-label">当前团队人数：</div>
          <div class="form-item-value">{{ seatState.current }}&nbsp;人</div>
        </div>
        <div class="form-item">
          <div class="form-item-label">本次购买席位：</div>
          <div class="form-item-value">{{ seatState?.pre }}&nbsp;个</div>
        </div>
        <div class="form-item">
          <div class="form-item-label">注意事项：</div>
          <div>
            <div class="form-item-value">• 被保留的成员将继续享有团队版权益</div>
            <div class="form-item-value">• 未被选择的成员将被移出团队，恢复为个人账号</div>
            <div class="form-item-value">• 被移出成员的团队资产(创作素材、模型配置等)将转移至您的管理员账号</div>
            <div class="form-item-value">• 被移出成员的个人资产不受影响</div>
          </div>
        </div>

        <div class="table-item">
          <div class="table-item-label"></div>
          <div class="table-item-value">
            <a-table
              :dataSource="groupStore.groupMembers"
              :columns="columns"
              :pagination="false"
              rowKey="userId"
              :row-selection="rowSelection"
              :loading="groupStore.loading"
              key="userId"
              :showHeader="false"
              :scroll="{ y: 250 }"
            >
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'account'">
                  {{ record.account ? privacyAccount(record.account) : "-" }}
                  {{ record.account == userStrore.userInfo.account ? "（当前账号）" : "" }}
                </template>
              </template>
            </a-table>
          </div>
        </div>
      </div>
      <div class="footer-box">
        <a-button class="cancle-button" :disabled="isCloseButtonDisabled" @click="onCloseButton" :loading="loading">保留当前成员</a-button>
        <a-button class="primary-button" @click="onCofirmButton">购买席位</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup lang="tsx">
import { computed, onMounted, ref } from "vue";
import useGroupStore from "@/store/group";
import { Table as ATable, message } from "ant-design-vue";
import type { TableColumnsType } from "ant-design-vue";
import { storeToRefs } from "pinia";
import { privacyAccount } from "@/views/group/utils";
import { useUserStore } from "@/store/user";
import { useTeamEditionStore } from "@/store/teamEdition";

interface Props {
  onClose: () => void;
}

defineProps<Props>();
const modalId = "group-retain-pop";
defineOptions({ modalId });

const visable = ref(false);
const loading = ref(false);
const groupStore = useGroupStore();
const userStrore = useUserStore();

const { groupState, seatState} = storeToRefs(groupStore);

const selectedRowKeys = ref<(string | number)[]>([]);

const columns = computed<TableColumnsType>(() => [
  {
    title: "成员",
    dataIndex: "nickname",
    key: "nickname",
    width: 200
  },
  {
    title: "手机号",
    dataIndex: "account",
    key: "account"
  }
]);

/**过滤掉管理员的成员列表 */

const rowSelection = computed(() => {
  const maxSeats = groupState.value?.preparHeadCount || 0;
  const currentUserId = userStrore.userInfo.id;

  // 确保当前账号始终被选中
  const currentUserSelected = selectedRowKeys.value.includes(currentUserId);
  if (!currentUserSelected && currentUserId) {
    selectedRowKeys.value = [...selectedRowKeys.value, currentUserId];
  }

  return {
    selectedRowKeys: selectedRowKeys.value,
    onChange: (keys: (string | number)[]) => {
      // 确保当前账号始终被选中
      if (currentUserId && !keys.includes(currentUserId)) {
        keys.push(currentUserId);
      }

      // 如果选择的成员数量超过本次购买席位，则限制为最大席位数量
      if (keys.length > maxSeats) {
        selectedRowKeys.value = keys.slice(0, maxSeats);
        message.error("当前可保留的团队成员已达到上限");
      } else {
        selectedRowKeys.value = keys;
      }
    },
    getCheckboxProps: (record: any) => ({
      disabled: record.userId === currentUserId
    })
  };
});

/**判断保留当前成员按钮是否禁用 */
const isCloseButtonDisabled = computed(() => {
  const maxSeats = groupState.value?.preparHeadCount || 0;
  return selectedRowKeys.value.length !== maxSeats;
});

onMounted(() => {
  visable.value = true;
});

/**保留当前成员 */
const onCloseButton = async () => {
  try {
    loading.value = true;
    let retainUserIds = selectedRowKeys.value.map((item) => item.toString());

    groupStore.retainCurrentMember(retainUserIds);
    message.success("团队成员配置成功");
    visable.value = false;
  } catch (error) {
  } finally {
    loading.value = false;
  }
};

/**购买席位 */
const onCofirmButton = () => {
  visable.value = false;
  const teamEditionStore = useTeamEditionStore();
  teamEditionStore.handleOpenTeamEdition();
};
</script>

<style scoped lang="less">
.modal-body {
  display: flex;
  flex-direction: column;

  .top-box {
    display: flex;
    padding: 12px 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    align-self: stretch;
    border-radius: 10px;
    background: rgba(235, 235, 245, 0.08);

    .top-box-text {
      color: rgba(236, 233, 242, 0.6);
      font-size: 14px;
      font-weight: 400;
      line-height: 20px; /* 142.857% */
    }

    .top-box-bold {
      color: #fff;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px; /* 142.857% */
    }
  }
  .content-box {
    display: flex;
    flex-direction: column;

    .form-item {
      display: flex;
      display: flex;
      padding: 16px 24px;
      align-items: flex-start;
      gap: 16px;
      align-self: stretch;

      .form-item-label {
        width: 100px;
        color: rgba(236, 233, 242, 0.6);
        text-align: right;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px; /* 142.857% */
        white-space: nowrap;
      }

      .form-item-value {
        color: #fff;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px; /* 142.857% */
      }
    }

    .table-item {
      display: flex;
      display: flex;
      padding: 16px 24px;
      align-items: flex-start;
      gap: 16px;
      align-self: stretch;
      .table-item-label {
        width: 100px;
        color: rgba(236, 233, 242, 0.6);
        text-align: right;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px; /* 142.857% */
        white-space: nowrap;
        flex-shrink: 0;
      }

      .table-item-value {
        flex: 1;
        position: relative;
        z-index: 5;

        :deep(.ant-table) {
          border-radius: 10px;
          background: rgba(60, 60, 67, 0.6);
          overflow: hidden;
          min-height: 250px;

          .ant-table-row,
          .ant-table-cell {
            border-color: rgba(235, 235, 245, 0.08);
          }

          .ant-table-thead {
            .ant-table-cell {
              padding: 12px 16px;
              color: rgba(236, 233, 242, 0.9);
              font-size: 14px;
              font-weight: 600;
              line-height: 32px;
              background: transparent;
              white-space: nowrap;

              &::before {
                display: none;
              }
            }
          }

          .ant-table-tbody {
            .ant-table-row {
              transition: all 0.4s;
              &:hover {
                background: rgba(60, 60, 67, 0.18);
              }
            }

            .ant-table-cell {
              padding: 12px 16px;
              line-height: 32px;
              font-size: 14px;
              white-space: nowrap;
              background: transparent;
              color: #fff;

              .ant-checkbox-inner {
                background-color: transparent;
                border-color: rgba(235, 235, 245, 0.3);
              }

              .ant-checkbox-checked .ant-checkbox-inner {
                background-color: #7a50cc;
                border-color: #7a50cc;
              }
            }
          }
        }
      }
    }
  }

  .footer-box {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    gap: 8px;

    .cancle-button {
      display: flex;
      padding: 10px 32px;
      justify-content: center;
      align-items: center;
      gap: 12px;
      color: rgba(236, 233, 242, 0.9);
      font-size: 14px;
      font-weight: 400;
      line-height: 20px; /* 142.857% */
      border-radius: 10px;
      border: 1px solid rgba(235, 235, 245, 0.15);
      height: auto;
      background: transparent;

      &:disabled {
        opacity: 0.5;
      }
    }

    .primary-button {
      display: flex;
      padding: 10px 40px;
      justify-content: center;
      align-items: center;
      gap: 12px;
      border-radius: 10px;
      height: auto;
      border: 1px solid rgba(235, 235, 245, 0.15);
      background: #8c5bec;

      height: auto;

      color: #fff;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px; /* 142.857% */
    }
  }
}
</style>

<style lang="less">
.group-retain-pop {
  .ant-modal-content {
    border-radius: 10px;
    border: 1px solid rgba(235, 235, 245, 0.08);
    background: #424248;
    backdrop-filter: blur(12px);
    padding: 0px;
  }

  .ant-modal-header {
    background: transparent;
    border-bottom: 1px solid rgba(235, 235, 245, 0.08);
    margin: 0;
  }
  .ant-modal-title {
    display: flex;
    height: 46px;
    padding: 16px 16px 12px 12px;
    align-items: center;
    gap: 12px;
    align-self: stretch;
    background-color: transparent;
    color: #fff;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
  }

  .ant-modal-body {
    padding: 16px; /* 统一由 .modal-body 控制内边距 */
  }
}
</style>
